import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
} from '@mui/material';
import {
  LocalHospital as DiseaseIcon,
  MedicalServices as TreatmentIcon,
  Add as AddIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_DISEASES, GET_TREATMENTS } from '../graphql/operations';
import { StatsCard, PageHeader } from './ui';
import TopDiagnoses from './TopDiagnoses';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: diseasesData, loading: diseasesLoading } = useQuery(GET_DISEASES, {
    variables: { limit: 10 }
  });
  
  const { data: treatmentsData, loading: treatmentsLoading } = useQuery(GET_TREATMENTS);

  const diseaseCount = diseasesData?.listDiseases?.items?.length || 0;
  const treatmentCount = treatmentsData?.listTreatments?.items?.length || 0;
  const recentDiseases = diseasesData?.listDiseases?.items?.slice(0, 5) || [];

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Monitor and manage your medical data"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/diseases/new')}
            sx={{ mb: { xs: 2, sm: 0 } }}
          >
            Add Disease
          </Button>
        }
      />
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Diseases"
            value={diseasesLoading ? '...' : diseaseCount}
            icon={DiseaseIcon}
            loading={diseasesLoading}
            color="primary"
            subtitle="Active medical conditions"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Treatments"
            value={treatmentsLoading ? '...' : treatmentCount}
            icon={TreatmentIcon}
            loading={treatmentsLoading}
            color="secondary"
            subtitle="Available treatment options"
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Active Sessions"
            value="24"
            icon={TrendingIcon}
            color="success"
            subtitle="Current diagnostic sessions"
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Accuracy Rate"
            value="94.2%"
            icon={TrendingIcon}
            color="warning"
            subtitle="Model performance"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <TopDiagnoses />
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Recent Diseases
                </Typography>
                {recentDiseases.length > 0 ? (
                  <List sx={{ pt: 0 }}>
                    {recentDiseases.map((disease: any) => (
                      <ListItem 
                        key={disease.id} 
                        sx={{ 
                          px: 0, 
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {disease.name}
                            </Typography>
                          }
                          secondary={`Status: ${disease.is_active ? 'Active' : 'Inactive'}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                    No diseases found. Start by creating your first disease entry.
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/diseases')}
                  sx={{ mt: 2 }}
                >
                  View All Diseases
                </Button>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="text"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/diseases/new')}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  >
                    Create new disease entry
                  </Button>
                  <Button
                    variant="text"
                    startIcon={<TreatmentIcon />}
                    onClick={() => navigate('/treatments/new')}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  >
                    Add treatment option
                  </Button>
                  <Button
                    variant="text"
                    startIcon={<TrendingIcon />}
                    onClick={() => navigate('/training-samples')}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  >
                    Review training samples
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
